import React, {
  useMemo,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import styled from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { withStyles, createStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import SwipeableViews from 'react-swipeable-views';
import { LocalizedProduct } from 'models/types/products';

import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ProductTab from 'components/product/ProductTab';
import { fetchBoothProducts, selectBoothProductsByBoothId } from 'models/boothProducts';
import { selectLocale } from 'models/localization';
import { TabsContext } from 'components/TabsContext';
import ProductDetailField from './ProductDetailField';

const Container = styled.div<{ isDesktop: boolean }>`
  width: ${({ isDesktop }) => (isDesktop ? '90%' : '100%')};
  max-width: 1200px;
  height: auto;
  margin: 0 auto;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  background-color: #FFF;
  letter-spacing: 0.3px;
  padding: 1px;
  margin-top: 40px;
  margin-bottom: 16px;

  .MuiTab-wrapper {
    font-size: ${({ isDesktop }) => (isDesktop ? '24px' : '18px')};
  }

  button.MuiButtonBase-root.MuiTab-root {
    margin-right: ${({ isDesktop }) => (isDesktop ? '40px' : '20px')};
  }
`;

const StyledTabs = ({ children, ...props }: any) => {
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const Custom = withStyles(createStyles({
    root: {
      margin: 24,
    },
    indicator: {
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'center',
      backgroundColor: 'transparent',
      height: '4px',
      '& > div': {
        maxWidth: 139,
        width: '100%',
        backgroundColor: themeColor || DEFAULT_THEME_COLOR,
      },
    },
  }))(Tabs);
  return <Custom {...props} TabIndicatorProps={{ children: <div /> }}>{children}</Custom>;
};

const StyledTab = ({ children, ...props }: any) => {
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const Custom = withStyles((theme) => ({
    root: {
      textTransform: 'none',
      color: '#A2AAB3',
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(24),
      fontFamily: 'Lato',
      padding: '0',
      minWidth: 'auto',
      '&:focus': {
        opacity: 1,
      },
    },
    selected: {
      color: themeColor || DEFAULT_THEME_COLOR,
    },
  }))(Tab);
  return <Custom {...props} disableRipple>{children}</Custom>;
};

const TabPanel = ({ children, value, index }: {
  children: React.ReactElement;
  value: number;
  index: number;
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
  >
    {value === index && (
      children
    )}
  </div>
);

interface TabElement {
  id: string | number;
  title: string;
  Element: () => React.ReactElement;
  condition: () => boolean;
}

const ProductDescriptionContainer = ({
  product: {
    details = {},
    boothId,
  },
}: {
  product: LocalizedProduct;
}) => {
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const locale = useSelector(selectLocale);
  const { t } = useTranslation();
  const { activeTabKey, setActiveTabKey } = useContext(TabsContext);
  const dispatch = useDispatch();
  const { products: { disableDetailMoreProducts, tabNames, disableDetailProductDetails } } = useSelector((s: any) => s.eventConfigs);

  useEffect(() => {
    dispatch(fetchBoothProducts(boothId));
  }, [boothId, dispatch]);

  const boothProducts = useSelector(selectBoothProductsByBoothId(boothId));
  const isBoothProductsFetching = useSelector((state) => (state as any).boothProducts.fetching, shallowEqual);

  const detailsTitle = useMemo(() => findAvailableLocale((tabNames || {}).details, locale) || t('product.details', 'Product Details'), [tabNames, locale, t]);
  const recommendationsTitle = useMemo(() => findAvailableLocale((tabNames || {}).recommendations, locale) || t('product.recommendations', 'More Products'), [tabNames, locale, t]);

  const onTabChange = useCallback((_, index) => setActiveTabKey(index as number), [setActiveTabKey]);

  if (disableDetailMoreProducts && disableDetailProductDetails) {
    return null;
  }

  return (
    <Container isDesktop={isDesktop}>
      <StyledTabs
        scrollButtons="off"
        variant="scrollable"
        value={activeTabKey}
        aria-label="Product Description Tabs"
        onChange={onTabChange}
      >
        {!disableDetailProductDetails && (
          <StyledTab
            label={detailsTitle}
          />
        )}
        {!disableDetailMoreProducts && (
          <StyledTab
            label={recommendationsTitle}
          />
        )}
      </StyledTabs>
      <SwipeableViews
        axis="x"
        index={activeTabKey}
        disabled
        ignoreNativeScroll
        resistance
      >
        {!disableDetailProductDetails && (
          <TabPanel key={detailsTitle} value={activeTabKey} index={0}>
            <>
              {Object.values(details).map(({ title, content }, detailIndex) => (
                <ProductDetailField title={title} key={detailIndex} content={content} />
              ))}
            </>
          </TabPanel>
        )}
        {!disableDetailMoreProducts && (
          <TabPanel key={recommendationsTitle} value={activeTabKey} index={1}>
            <ProductTab boothProducts={boothProducts} fetching={isBoothProductsFetching} />
          </TabPanel>
        )}
      </SwipeableViews>
    </Container>
  );
};

export default ProductDescriptionContainer;
